// ProposalImportModal — upload a subcontractor PDF, scan with Claude, preview & embed line items

import React, { useState, useRef, useCallback } from "react";
import { supabase } from "../lib/supabase";
import type { Theme } from "../lib/theme";
import * as Icon from "./OEIcons";

export interface EmbedItem {
  description: string;
  qty: number;
  unit: string;
  unitCost: number;
  proposal: string;
  notes: string;
  altType: string;   // "" | "Alt" | "Void"
  altStatus: string; // "" | "TBD" | "Accepted" | "Void"
  include: boolean;
}

interface TradeSource { id: string; tradePartner: string; estimateDate: string; notes: string; }

interface Props {
  divId: string;
  divName: string;
  subId: string;
  subName: string;
  proposals: Array<{ id: string; name: string; color: number }>;
  proposalColors: Array<{ bg: string; text: string; bar: string }>;
  defaultProposal: string;
  tradeSources: TradeSource[];
  T: Theme;
  font: string;
  onEmbed: (items: Omit<EmbedItem, "include">[], sourceInfo?: { tradePartner: string; estimateDate: string }) => void;
  onClose: () => void;
}

type Stage = "idle" | "scanning" | "preview" | "error";

const UNITS = ["LS","EA","SF","LF","CY","CF","SY","MBF","TON","HR","DAY","GAL","LB","MO"];

export default function ProposalImportModal({
  divName, subName, proposals, proposalColors, defaultProposal, tradeSources, T, font, onEmbed, onClose,
}: Props) {
  const [stage, setStage] = useState<Stage>("idle");
  const [items, setItems] = useState<EmbedItem[]>([]);
  const [errorMsg, setErrorMsg] = useState("");
  const [fileName, setFileName] = useState("");
  const [isDragging, setIsDragging] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  // Trade partner info
  const [tradePartner, setTradePartner] = useState("");
  const [estimateDate, setEstimateDate] = useState("");

  const ACCENT = T.borderAccent;
  const BG = T.bgModal;
  const BG2 = T.bgInput;
  const BORDER = T.borderPrimary;

  const processFile = useCallback(async (file: File) => {
    if (!file || file.type !== "application/pdf") {
      setErrorMsg("Please select a PDF file.");
      setStage("error");
      return;
    }
    setFileName(file.name);
    setStage("scanning");
    setErrorMsg("");

    try {
      // Read as base64
      const base64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const result = reader.result as string;
          resolve(result.split(",")[1]); // strip data:application/pdf;base64,
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      if (!supabase) throw new Error("Supabase not configured.");

      const { data, error } = await supabase.functions.invoke("parse-proposal", {
        body: { pdfBase64: base64, subcategoryName: subName, divisionName: divName },
      });

      if (error) throw new Error(error.message || "Edge function error");
      if (data.error) throw new Error(data.error + (data.detail ? `: ${data.detail}` : ""));

      const parsed: EmbedItem[] = (data.items || []).map((it: any) => ({
        description: it.description,
        qty: it.qty,
        unit: it.unit,
        unitCost: it.unitCost,
        notes: it.notes,
        proposal: defaultProposal,
        altType: "",
        altStatus: "",
        include: true,
      }));

      if (parsed.length === 0) {
        setErrorMsg("Claude couldn't find any line items in this PDF. Try a clearer or more detailed proposal.");
        setStage("error");
        return;
      }

      setItems(parsed);
      setStage("preview");
    } catch (err: any) {
      setErrorMsg(err.message || "Something went wrong.");
      setStage("error");
    }
  }, [subName, divName, defaultProposal]);

  const handleFile = (f: File | null | undefined) => { if (f) processFile(f); };

  const updateItem = (i: number, field: keyof EmbedItem, value: any) => {
    setItems(prev => prev.map((it, idx) => idx === i ? { ...it, [field]: value } : it));
  };

  const toggleAll = (val: boolean) => setItems(prev => prev.map(it => ({ ...it, include: val })));

  const handleEmbed = () => {
    const toEmbed = items.filter(it => it.include).map(({ include: _, ...rest }) => rest);
    if (toEmbed.length === 0) return;
    const sourceInfo = tradePartner.trim() ? { tradePartner: tradePartner.trim(), estimateDate } : undefined;
    onEmbed(toEmbed, sourceInfo);
    onClose();
  };

  const includedCount = items.filter(i => i.include).length;
  const includedTotal = items.filter(i => i.include).reduce((s, i) => s + i.qty * i.unitCost, 0);

  // ── Shared styles ──────────────────────────────────────────────────────────
  const labelStyle: React.CSSProperties = { fontSize: 10, color: T.textWeak, letterSpacing: ".1em", textTransform: "uppercase", fontWeight: 500 };
  const inputStyle: React.CSSProperties = { background: BG2, border: `1px solid ${BORDER}`, color: T.textSecondary, borderRadius: 3, padding: "4px 7px", fontFamily: font, fontSize: 12, outline: "none", width: "100%" };

  return (
    <div style={{ position: "fixed", inset: 0, background: T.bgModalBackdrop, zIndex: 9000, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
      <div style={{ background: BG, border: `1px solid ${BORDER}`, borderRadius: 6, width: "100%", maxWidth: stage === "preview" ? 860 : 520, maxHeight: "90vh", display: "flex", flexDirection: "column", fontFamily: font, boxShadow: "0 24px 64px #00000060" }}>

        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 20px", borderBottom: `1px solid ${BORDER}`, flexShrink: 0 }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: T.textPrimary }}>Import Subcontractor Proposal</div>
            <div style={{ fontSize: 11, color: T.textWeak, marginTop: 2 }}>
              {divName} — <span style={{ color: ACCENT }}>{subName}</span>
              {tradePartner && <span style={{ marginLeft: 8, color: T.borderAccent, fontWeight: 600 }}>· {tradePartner}{estimateDate ? ` (${estimateDate})` : ""}</span>}
            </div>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: T.textWeak, cursor: "pointer", fontSize: 16, lineHeight: 1, padding: "4px 8px" }}>✕</button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: "auto", padding: "20px" }}>

          {/* ── IDLE: upload area ── */}
          {stage === "idle" && (
            <div>
              {/* Trade Partner info */}
              <div style={{ marginBottom: 18, padding: "14px 16px", background: T.bgHover, border: `1px solid ${T.borderPrimary}`, borderRadius: 6 }}>
                <div style={{ fontSize: 10, color: T.textWeak, letterSpacing: ".1em", textTransform: "uppercase", marginBottom: 10, fontWeight: 500 }}>Trade Partner</div>
                <div style={{ display: "flex", gap: 10 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 10, color: T.textMuted, marginBottom: 4 }}>Company / Trade Name</div>
                    <input
                      list="trade-source-list"
                      value={tradePartner}
                      onChange={e => setTradePartner(e.target.value)}
                      placeholder="e.g. ABC Concrete, Smith Electrical..."
                      style={{ ...inputStyle, fontSize: 12, fontWeight: 500 }}
                    />
                    <datalist id="trade-source-list">
                      {tradeSources.map(s => <option key={s.id} value={s.tradePartner} />)}
                    </datalist>
                  </div>
                  <div style={{ width: 150 }}>
                    <div style={{ fontSize: 10, color: T.textMuted, marginBottom: 4 }}>Estimate Date</div>
                    <input
                      type="date"
                      value={estimateDate}
                      onChange={e => setEstimateDate(e.target.value)}
                      style={{ ...inputStyle, fontSize: 12 }}
                    />
                  </div>
                </div>
              </div>
              <div
                onDragOver={e => { e.preventDefault(); setIsDragging(true); }}
                onDragLeave={() => setIsDragging(false)}
                onDrop={e => { e.preventDefault(); setIsDragging(false); handleFile(e.dataTransfer.files[0]); }}
                onClick={() => fileRef.current?.click()}
                style={{
                  border: `2px dashed ${isDragging ? ACCENT : BORDER}`,
                  borderRadius: 6, padding: "48px 24px", textAlign: "center", cursor: "pointer",
                  background: isDragging ? `${ACCENT}08` : T.bgHover,
                  transition: "border-color .15s, background .15s",
                }}
              >
                <div style={{ marginBottom: 12 }}><Icon.FileText size={32} /></div>
                <div style={{ fontSize: 14, fontWeight: 600, color: T.textPrimary, marginBottom: 6 }}>Drop a PDF here</div>
                <div style={{ fontSize: 12, color: T.textWeak, marginBottom: 16 }}>or click to browse</div>
                <div style={{ display: "inline-block", fontSize: 11, padding: "6px 18px", background: ACCENT, color: T.textOnGold, borderRadius: 3, fontWeight: 600, cursor: "pointer" }}>
                  Select PDF
                </div>
                <input ref={fileRef} type="file" accept="application/pdf" style={{ display: "none" }} onChange={e => handleFile(e.target.files?.[0])} />
              </div>
              <div style={{ marginTop: 14, fontSize: 11, color: T.textWeak, textAlign: "center" }}>
                Claude will read the proposal and extract line items with quantities and unit costs.
              </div>
            </div>
          )}

          {/* ── SCANNING ── */}
          {stage === "scanning" && (
            <div style={{ textAlign: "center", padding: "48px 24px" }}>
              <div style={{ fontSize: 24, marginBottom: 16 }}>
                <span style={{ display: "inline-block", animation: "spin 1.2s linear infinite" }}>⟳</span>
              </div>
              <div style={{ fontSize: 14, fontWeight: 600, color: T.textPrimary, marginBottom: 6 }}>Reading proposal with Claude...</div>
              <div style={{ fontSize: 11, color: T.textWeak }}>{fileName}</div>
              <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
            </div>
          )}

          {/* ── ERROR ── */}
          {stage === "error" && (
            <div style={{ textAlign: "center", padding: "32px 24px" }}>
              <div style={{ fontSize: 24, marginBottom: 12 }}>⚠️</div>
              <div style={{ fontSize: 13, fontWeight: 600, color: T.negative, marginBottom: 8 }}>Import failed</div>
              <div style={{ fontSize: 12, color: T.textWeak, marginBottom: 24, maxWidth: 380, margin: "0 auto 24px" }}>{errorMsg}</div>
              <button onClick={() => { setStage("idle"); setErrorMsg(""); }} style={{ background: ACCENT, color: T.textOnGold, border: "none", borderRadius: 3, padding: "8px 20px", cursor: "pointer", fontFamily: font, fontSize: 12, fontWeight: 600 }}>
                Try Again
              </button>
            </div>
          )}

          {/* ── PREVIEW ── */}
          {stage === "preview" && (
            <div>
              {/* Controls row */}
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}>
                <div style={{ fontSize: 12, color: T.textWeak }}>
                  <span style={{ color: T.textPrimary, fontWeight: 600 }}>{items.length}</span> items extracted from <span style={{ color: ACCENT }}>{fileName}</span>
                </div>
                <div style={{ marginLeft: "auto", display: "flex", gap: 8, alignItems: "center" }}>
                  <button onClick={() => toggleAll(true)} style={{ fontSize: 10, padding: "3px 8px", background: "none", border: `1px solid ${BORDER}`, color: T.textWeak, borderRadius: 3, cursor: "pointer", fontFamily: font }}>Select All</button>
                  <button onClick={() => toggleAll(false)} style={{ fontSize: 10, padding: "3px 8px", background: "none", border: `1px solid ${BORDER}`, color: T.textWeak, borderRadius: 3, cursor: "pointer", fontFamily: font }}>Deselect All</button>
                </div>
              </div>

              {/* Table */}
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                <thead>
                  <tr style={{ borderBottom: `1px solid ${BORDER}` }}>
                    <th style={{ ...labelStyle, padding: "6px 8px", width: 24 }}></th>
                    <th style={{ ...labelStyle, padding: "6px 8px", textAlign: "left" }}>Description</th>
                    <th style={{ ...labelStyle, padding: "6px 8px", textAlign: "right", width: 60 }}>Qty</th>
                    <th style={{ ...labelStyle, padding: "6px 8px", textAlign: "left", width: 70 }}>Unit</th>
                    <th style={{ ...labelStyle, padding: "6px 8px", textAlign: "right", width: 90 }}>Unit Cost</th>
                    <th style={{ ...labelStyle, padding: "6px 8px", textAlign: "right", width: 90 }}>Total</th>
                    <th style={{ ...labelStyle, padding: "6px 8px", textAlign: "left", width: 140 }}>Package</th>
                    <th style={{ ...labelStyle, padding: "6px 8px", textAlign: "left", width: 120 }}>Alt Type</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((it, i) => {
                    const total = it.qty * it.unitCost;
                    const pc = proposalColors[proposals.find(p => p.id === it.proposal)?.color ?? 0];
                    return (
                      <tr key={i} style={{ borderBottom: `1px solid ${T.bgHeader}`, opacity: it.include ? 1 : 0.4, transition: "opacity .15s" }}>
                        {/* checkbox */}
                        <td style={{ padding: "6px 8px", textAlign: "center" }}>
                          <input type="checkbox" checked={it.include} onChange={e => updateItem(i, "include", e.target.checked)}
                            style={{ accentColor: ACCENT, cursor: "pointer" }} />
                        </td>
                        {/* description */}
                        <td style={{ padding: "4px 6px" }}>
                          <input value={it.description} onChange={e => updateItem(i, "description", e.target.value)}
                            style={{ ...inputStyle, fontSize: 12 }} />
                          {it.notes && <div style={{ fontSize: 10, color: T.textWeak, marginTop: 2, paddingLeft: 2 }}>{it.notes}</div>}
                        </td>
                        {/* qty */}
                        <td style={{ padding: "4px 6px" }}>
                          <input type="number" value={it.qty} onChange={e => updateItem(i, "qty", parseFloat(e.target.value) || 0)}
                            style={{ ...inputStyle, textAlign: "right", width: 54 }} />
                        </td>
                        {/* unit */}
                        <td style={{ padding: "4px 6px" }}>
                          <select value={it.unit} onChange={e => updateItem(i, "unit", e.target.value)}
                            style={{ ...inputStyle, width: 64, cursor: "pointer" }}>
                            {UNITS.map(u => <option key={u} value={u}>{u}</option>)}
                            {!UNITS.includes(it.unit) && <option value={it.unit}>{it.unit}</option>}
                          </select>
                        </td>
                        {/* unitCost */}
                        <td style={{ padding: "4px 6px" }}>
                          <input type="number" value={it.unitCost} onChange={e => updateItem(i, "unitCost", parseFloat(e.target.value) || 0)}
                            style={{ ...inputStyle, textAlign: "right", width: 82 }} />
                        </td>
                        {/* total */}
                        <td style={{ padding: "4px 8px", textAlign: "right", fontWeight: 600, color: T.textSecondary, whiteSpace: "nowrap" }}>
                          ${total.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                        </td>
                        {/* proposal */}
                        <td style={{ padding: "4px 6px" }}>
                          <select value={it.proposal} onChange={e => updateItem(i, "proposal", e.target.value)}
                            style={{ ...inputStyle, width: 130, cursor: "pointer", color: pc?.text || T.textSecondary, background: pc?.bg || BG2, fontWeight: 600 }}>
                            {proposals.map(p => {
                              const c = proposalColors[p.color];
                              return <option key={p.id} value={p.id} style={{ background: T.bgInput, color: T.textSecondary }}>{p.name}</option>;
                            })}
                          </select>
                        </td>
                        {/* alt type */}
                        <td style={{ padding: "4px 6px" }}>
                          {(()=>{
                            const altDropVal = it.altType==="Alt"&&it.altStatus==="Accepted" ? "Alt-Accepted" : it.altType||"";
                            const altColor = altDropVal==="Alt-Accepted" ? T.positive : altDropVal==="Alt" ? T.textTertiary : altDropVal==="Void" ? T.textMuted : T.textWeak;
                            return (
                              <select value={altDropVal}
                                onChange={e=>{
                                  const v=e.target.value;
                                  if(v==="Alt-Accepted"){ updateItem(i,"altType","Alt"); updateItem(i,"altStatus","Accepted"); }
                                  else if(v==="Alt"){ updateItem(i,"altType","Alt"); updateItem(i,"altStatus","TBD"); }
                                  else if(v==="Void"){ updateItem(i,"altType","Void"); updateItem(i,"altStatus","Void"); }
                                  else { updateItem(i,"altType",""); updateItem(i,"altStatus",""); }
                                }}
                                style={{ ...inputStyle, width: 112, cursor: "pointer", color: altColor, fontWeight: altDropVal ? 600 : 400 }}>
                                <option value="" style={{ background: T.bgInput, color: T.textSecondary }}>Base Bid</option>
                                <option value="Alt" style={{ background: T.bgInput, color: T.textSecondary }}>Alternate — TBD</option>
                                <option value="Alt-Accepted" style={{ background: T.bgInput, color: T.textSecondary }}>Accepted ✓</option>
                                <option value="Void" style={{ background: T.bgInput, color: T.textSecondary }}>Rejected ✕</option>
                              </select>
                            );
                          })()}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Footer */}
        {stage === "preview" && (
          <div style={{ borderTop: `1px solid ${BORDER}`, padding: "14px 20px", display: "flex", alignItems: "center", gap: 12, flexShrink: 0 }}>
            <div style={{ fontSize: 11, color: T.textWeak }}>
              <span style={{ color: T.textPrimary, fontWeight: 600 }}>{includedCount}</span> of {items.length} items selected
              {includedTotal > 0 && <span style={{ color: ACCENT, marginLeft: 8, fontWeight: 600 }}>
                ${includedTotal.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
              </span>}
            </div>
            <div style={{ marginLeft: "auto", display: "flex", gap: 10 }}>
              <button onClick={onClose} style={{ background: "none", border: `1px solid ${BORDER}`, color: T.textWeak, borderRadius: 3, padding: "8px 16px", cursor: "pointer", fontFamily: font, fontSize: 12 }}>
                Cancel
              </button>
              <button onClick={handleEmbed} disabled={includedCount === 0}
                style={{ background: includedCount > 0 ? ACCENT : T.bgHover, color: includedCount > 0 ? T.textOnGold : T.textWeak, border: "none", borderRadius: 3, padding: "8px 20px", cursor: includedCount > 0 ? "pointer" : "default", fontFamily: font, fontSize: 12, fontWeight: 700, transition: "opacity .15s" }}>
                Confirm &amp; Embed {includedCount > 0 ? `${includedCount} Item${includedCount !== 1 ? "s" : ""}` : ""}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
